import { FC, useEffect, useState } from 'react';
import { orderBy } from 'lodash';
import { Link } from 'react-router-dom';
import { FormInput, Modal, SearchInput } from '@praktio/common-ui';

import './CourseOverrides.scss';

interface IAddCourseOverrideModalProps {
  courses: ICourse[];
  /** The full catalog, before courses that already have an override are filtered out. */
  totalCourseCount: number;
  show: boolean;
  onAdd: (courseId: string) => void;
  onHide: () => void;
}

/**
 * Picks a course to start overriding. Only courses without an override are offered — a course already
 * in the list is edited in place, never added twice.
 */
const AddCourseOverrideModal: FC<IAddCourseOverrideModalProps> = ({
  courses,
  totalCourseCount,
  show,
  onAdd,
  onHide,
}) => {
  const [search, setSearch] = useState('');

  // The filter is per-visit, not per-session: reopening the picker should show the whole list again.
  useEffect(() => {
    if (show) {
      setSearch('');
    }
  }, [show]);

  const term = search.trim().toLowerCase();
  const matches = term ? courses.filter((c) => c.title?.toLowerCase().includes(term)) : courses;
  const hasNoMatches = matches.length === 0;

  // "Every course already has one" is only true if there were courses to begin with. Keyed off the
  // FULL catalog, so an empty or failed course fetch does not produce a confidently wrong sentence.
  const emptyMessage = term
    ? 'No courses match your search.'
    : totalCourseCount === 0
    ? 'No courses are available to override.'
    : 'Every course already has its own description for this team.';

  return (
    <Modal
      show={show}
      onHide={onHide}
      hideFooter
      title="Add Course Override"
      description="Choose any course to give it its own description for this team. Courses that already have one are not listed."
      content={
        <div>
          <SearchInput
            value={search}
            placeholder="Search courses"
            onChange={(event) => setSearch(event.target.value)}
            onSearch={() => null}
            onClear={() => setSearch('')}
          />
          <div className="CourseOverrides-picker-list mt-3">
            {matches.map((course) => (
              <button
                key={course._id}
                type="button"
                className="CourseOverrides-picker-option"
                onClick={() => onAdd(course._id as string)}
              >
                {course.title}
              </button>
            ))}
            {hasNoMatches ? <div>{emptyMessage}</div> : null}
          </div>
        </div>
      }
    />
  );
};

interface IProps {
  /** True on a top-level team, which owns the values. A subteam displays them read-only. */
  ownsOverrides: boolean;
  /** The full course catalog — an override may be set against any course. */
  overridableCourses: ICourse[];
  /** False until the catalog has arrived, so a missing title can be told from a deleted course. */
  coursesAreLoaded: boolean;
  /** The rows to display — the team's own edits when it owns them, the inherited set otherwise. */
  overrides: ICourseOverride[];
  /** The top-level team a subteam inherits from, when it is known. */
  inheritedFrom?: ITeam;
  onChange: (overrides: ICourseOverride[]) => void;
  showAddModal: boolean;
  setShowAddModal: (show: boolean) => void;
}

const CourseOverrides: FC<IProps> = ({
  ownsOverrides,
  overridableCourses,
  coursesAreLoaded,
  overrides,
  inheritedFrom,
  onChange,
  showAddModal,
  setShowAddModal,
}) => {
  /**
   * The course's title, or a stand-in that says WHICH kind of missing it is. Before the catalog
   * loads every row would otherwise read "Course"; afterwards, a row whose course has been deleted
   * from the catalog keeps that label permanently with nothing marking it as broken.
   */
  const titleFor = (courseId?: string) => {
    const title = overridableCourses.find((c) => c._id?.toString() === courseId?.toString())?.title;

    if (title) {
      return title;
    }

    return coursesAreLoaded ? 'Course no longer available' : 'Loading…';
  };

  // Only rows that exist are rendered, so a catalog of 30-40 courses does not become 30-40 empty
  // text boxes. A course is added through the picker and removed with the control on its row.
  const rows = overrides.filter((o) => !!o.courseId);

  // Sorted for DISPLAY only, so that saving never rewrites the order the rows are stored in — the
  // handlers below map and filter `rows`, not this. (It would not mark the form dirty either way:
  // dirtiness compares `settings` against `prevSettings`, and both would hold the same sorted array.)
  const sortedRows = orderBy(rows, [(o) => titleFor(o.courseId).toLowerCase()], ['asc']);

  const availableCourses = orderBy(
    overridableCourses.filter(
      (c) => !rows.find((o) => o.courseId?.toString() === c._id?.toString()),
    ),
    [(c) => c.title?.toLowerCase()],
    ['asc'],
  );

  const onDescriptionChange = (courseId: string, description: string) =>
    onChange(rows.map((o) => (o.courseId === courseId ? { ...o, description } : o)));

  const onRemove = (courseId: string) => onChange(rows.filter((o) => o.courseId !== courseId));

  if (!ownsOverrides) {
    return (
      <>
        {!!inheritedFrom && (
          <div className="mb-3 text-left">
            These descriptions are inherited from the{' '}
            <Link to={`/admin/teams/${inheritedFrom._id}`}>{inheritedFrom.name}</Link> team and can
            only be adjusted in that team&apos;s settings.
          </div>
        )}
        {rows.length === 0 ? (
          <div>No course descriptions are overridden for this team.</div>
        ) : (
          sortedRows.map((override) => (
            <div className="CourseOverrides-inherited" key={override.courseId}>
              {/* A heading rather than a form label: there is no input here, and at label size the
                  course name was lost against the description it introduces. */}
              <div className="CourseOverrides-inherited-title">{titleFor(override.courseId)}</div>
              {/* Shown as the learner will read it, rather than as raw markup. Same trust level as
                  the learner-side render in CourseInfo: the content is admin-authored HTML that is
                  already output this way on the course information page. */}
              <div
                className="CourseOverrides-inherited-body"
                dangerouslySetInnerHTML={{ __html: override.description || '' }}
              />
            </div>
          ))
        )}
      </>
    );
  }

  return (
    <>
      {rows.length === 0 && (
        <div>
          No course descriptions are overridden for this team. Learners see each course&apos;s own
          description.
        </div>
      )}
      {sortedRows.map((override, index) => (
        <FormInput
          key={override.courseId}
          type="textarea"
          fieldName={`courseOverride-${override.courseId}`}
          label={titleFor(override.courseId)}
          placeholder="Use the course description"
          value={override.description}
          // The label renders as a block element and labelAdditions follows it as a sibling, so the
          // wrapper has to be a flex row for the control to sit beside the course name.
          labelWrapperClassName="CourseOverrides-label d-flex align-items-center"
          labelAdditions={
            // A real button, so it is focusable and Enter/Space-operable without hand-rolling either.
            // The label names the course: two rows differ only by which one they remove, and the
            // icon alone announces as "delete".
            <button
              type="button"
              className="CourseOverrides-remove"
              aria-label={`Remove the override for ${titleFor(override.courseId)}`}
              title={`Remove the override for ${titleFor(override.courseId)}`}
              onClick={() => onRemove(override.courseId)}
            >
              <span className="material-icons" aria-hidden="true">
                delete
              </span>
            </button>
          }
          onChange={(event: { target: any }) =>
            onDescriptionChange(override.courseId, event.target.value)
          }
          lastInSection={index === sortedRows.length - 1}
        />
      ))}
      <AddCourseOverrideModal
        courses={availableCourses}
        totalCourseCount={overridableCourses.length}
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onAdd={(courseId) => {
          onChange([...rows, { courseId, description: '' }]);
          setShowAddModal(false);
        }}
      />
    </>
  );
};

export default CourseOverrides;
